/**
 * The global root redux state of the app.
 *
 * We define this explicitly here so that it is easier to read and follow the code,
 * and also to guard against human errors. Had we instead used `ReturnType` to
 * infer the global state, typescript (at least in this app) wouldn't be able
 * to verify that the `state` matches the return type in each reducer, so it's
 * possible to accidentally return a new state that has an incompatible type.
 *
 * By explicitly defining the entire state here, we can use the `RootReducer` utility
 * below to assert the type of each reducer.
 */

/**
 * Assertion on the shape of the root reducer. This helps typescript infer the types of
 * `state`, `action`, and the return type in each reducer.
 */
export let ActionType;
/**
 * An union of the actions in the app.
 *
 * We explicitedly define the ActionType alongside its payload so that we can easily check
 * the payload in switch case or similar statement structure.
 */

(function (ActionType) {
  ActionType["UPDATE_SEARCH_PARAMS"] = "UPDATE_SEARCH_PARAMS";
  ActionType["FETCH_CLAIM_BOXES_REQUESTED"] = "FETCH_CLAIM_BOXES_REQUESTED";
  ActionType["FETCH_CLAIM_BOXES_SUCCESS"] = "FETCH_CLAIM_BOXES_SUCCESS";
  ActionType["FETCH_CLAIM_BOXES_FAILURE"] = "FETCH_CLAIM_BOXES_FAILURE";
  ActionType["FETCH_SEARCH_RESULT_REQUESTED"] = "FETCH_SEARCH_RESULT_REQUESTED";
  ActionType["FETCH_SEARCH_RESULT_SUCCESS"] = "FETCH_SEARCH_RESULT_SUCCESS";
  ActionType["FETCH_SEARCH_RESULT_FAILURE"] = "FETCH_SEARCH_RESULT_FAILURE";
  ActionType["UPDATE_SEARCH_RENTAL_PARAMS"] = "UPDATE_SEARCH_PARAMS";
  ActionType["FETCH_SEARCH_RENTAL_RESULT_REQUESTED"] = "FETCH_SEARCH_RENTAL_RESULT_REQUESTED";
  ActionType["FETCH_SEARCH_RENTAL_RESULT_SUCCESS"] = "FETCH_SEARCH_RENTAL_RESULT_SUCCESS";
  ActionType["FETCH_SEARCH_RENTAL_RESULT_FAILURE"] = "FETCH_SEARCH_RENTAL_RESULT_FAILURE";
  ActionType["FETCH_AVAILABLE_HERO_INVENTORY_RESULT_REQUESTED"] = "FETCH_AVAILABLE_HERO_INVENTORY_RESULT_REQUESTED";
  ActionType["FETCH_AVAILABLE_HERO_INVENTORY_RESULT_SUCCESS"] = "FETCH_AVAILABLE_HERO_INVENTORY_RESULT_SUCCESS";
  ActionType["FETCH_AVAILABLE_HERO_INVENTORY_RESULT_FAILURE"] = "FETCH_AVAILABLE_HERO_INVENTORY_RESULT_FAILURE";
  ActionType["INVALIDATE_HERO_DETAIL"] = "INVALIDATE_HERO_DETAIL";
  ActionType["FETCH_HERO_DETAIL_REQUESTED"] = "FETCH_HERO_DETAIL_REQUESTED";
  ActionType["FETCH_HERO_DETAIL_SUCCESS"] = "FETCH_HERO_DETAIL_SUCCESS";
  ActionType["FETCH_HERO_DETAIL_FAILURE"] = "FETCH_HERO_DETAIL_FAILURE";
  ActionType["FETCH_HERO_UPGRADE_COST_REQUESTED"] = "FETCH_HERO_UPGRADE_COST_REQUESTED";
  ActionType["FETCH_HERO_UPGRADE_COST_SUCCESS"] = "FETCH_HERO_UPGRADE_COST_SUCCESS";
  ActionType["FETCH_HERO_UPGRADE_COST_FAILURE"] = "FETCH_HERO_UPGRADE_COST_FAILURE";
  ActionType["UPGRADE_HERO_REQUESTED"] = "UPGRADE_HERO_REQUESTED";
  ActionType["UPGRADE_HERO_SUCCESS"] = "UPGRADE_HERO_SUCCESS";
  ActionType["UPGRADE_HERO_FAILURE"] = "UPGRADE_HERO_FAILURE";
  ActionType["FETCH_TRANSACTION_CONFIRM_REQUESTED"] = "FETCH_TRANSACTION_CONFIRM_REQUESTED";
  ActionType["FETCH_TRANSACTION_CONFIRM_SUCCESS"] = "FETCH_TRANSACTION_CONFIRM_SUCCESS";
  ActionType["FETCH_TRANSACTION_CONFIRM_FAILURE"] = "FETCH_TRANSACTION_CONFIRM_FAILUREF";
  ActionType["INVALIDATE_CHECKOUT_ITEM"] = "INVALIDATE_CHECKOUT_ITEM";
  ActionType["CHECKOUT_ITEM_REQUESTED"] = "CHECKOUT_ITEM_REQUESTED";
  ActionType["CHECKOUT_ITEM_SUCCESS"] = "CHECKOUT_ITEM_SUCCESS";
  ActionType["CHECKOUT_ITEM_FAILURE"] = "CHECKOUT_ITEM_FAILURE";
  ActionType["FETCH_SALE_HISTORY_LIST_REQUESTED"] = "FETCH_SALE_HISTORY_LIST_REQUESTED";
  ActionType["FETCH_SALE_HISTORY_LIST_SUCCESS"] = "FETCH_SALE_HISTORY_LIST_SUCCESS";
  ActionType["FETCH_SALE_HISTORY_LIST_FAILURE"] = "FETCH_SALE_HISTORY_LIST_FAILURE";
  ActionType["USER_SIGN_OUT"] = "USER_SIGN_OUT";
  ActionType["INVALIDATE_SELL_ITEM"] = "INVALIDATE_SELL_ITEM";
  ActionType["SELL_ITEM_REQUESTED"] = "SELL_ITEM_REQUESTED";
  ActionType["SELL_ITEM_SUCCESS"] = "SELL_ITEM_SUCCESS";
  ActionType["SELL_ITEM_FAILURE"] = "SELL_ITEM_FAILURE";
  ActionType["FETCH_SALE_ITEM_TRANSACTION_CONFIRM_REQUESTED"] = "FETCH_SALE_ITEM_TRANSACTION_CONFIRM_REQUESTED";
  ActionType["FETCH_SALE_ITEM_TRANSACTION_CONFIRM_SUCCESS"] = "FETCH_SALE_ITEM_TRANSACTION_CONFIRM_SUCCESS";
  ActionType["FETCH_SALE_ITEM_TRANSACTION_CONFIRM_FAILURE"] = "FETCH_SALE_ITEM_TRANSACTION_CONFIRM_FAILURE";
  ActionType["FETCH_PAYMENT_TOKEN_REQUESTED"] = "FETCH_PAYMENT_TOKEN_REQUESTED";
  ActionType["FETCH_PAYMENT_TOKEN_SUCCESS"] = "FETCH_PAYMENT_TOKEN_SUCCESS";
  ActionType["FETCH_PAYMENT_TOKEN_FAILURE"] = "FETCH_PAYMENT_TOKEN_FAILUREF";
  ActionType["INVALIDATE_STOP_SELLING_ITEM"] = "INVALIDATE_STOP_SELLING_ITEM";
  ActionType["STOP_SELLING_ITEM_REQUESTED"] = "STOP_SELLING_ITEM_REQUESTED";
  ActionType["STOP_SELLING_ITEM_SUCCESS"] = "STOP_SELLING_ITEM_SUCCESS";
  ActionType["STOP_SELLING_ITEM_FAILURE"] = "STOP_SELLING_ITEM_FAILURE";
  ActionType["FETCH_THETAN_BOX_DETAIL_REQUESTED"] = "FETCH_THETAN_BOX_DETAIL_REQUESTED";
  ActionType["FETCH_THETAN_BOX_DETAIL_SUCCESS"] = "FETCH_THETAN_BOX_DETAIL_SUCCESS";
  ActionType["FETCH_THETAN_BOX_DETAIL_FAILURE"] = "FETCH_THETAN_BOX_DETAIL_FAILURE";
  ActionType["FETCH_USER_GIFT_BOX_DETAIL_REQUESTED"] = "FETCH_USER_GIFT_BOX_DETAIL_REQUESTED";
  ActionType["FETCH_USER_GIFT_BOX_DETAIL_SUCCESS"] = "FETCH_USER_GIFT_BOX_DETAIL_SUCCESS";
  ActionType["FETCH_USER_GIFT_BOX_DETAIL_FAILURE"] = "FETCH_USER_GIFT_BOX_DETAIL_FAILURE";
  ActionType["FETCH_THETAN_BOX_DASHBOARD_REQUESTED"] = "FETCH_THETAN_BOX_DASHBOARD_REQUESTED";
  ActionType["FETCH_THETAN_BOX_DASHBOARD_SUCCESS"] = "FETCH_THETAN_BOX_DASHBOARD_SUCCESS";
  ActionType["FETCH_THETAN_BOX_DASHBOARD_FAILURE"] = "FETCH_THETAN_BOX_DASHBOARD_FAILURE";
  ActionType["FETCH_GIFT_BOX_DASHBOARD_REQUESTED"] = "FETCH_GIFT_BOX_DASHBOARD_REQUESTED";
  ActionType["FETCH_GIFT_BOX_DASHBOARD_SUCCESS"] = "FETCH_GIFT_BOX_DASHBOARD_SUCCESS";
  ActionType["FETCH_GIFT_BOX_DASHBOARD_FAILURE"] = "FETCH_GIFT_BOX_DASHBOARD_FAILURE";
  ActionType["FETCH_THETAN_BOX_DASHBOARD_DETAIL_REQUESTED"] = "FETCH_THETAN_BOX_DASHBOARD_DETAIL_REQUESTED";
  ActionType["FETCH_THETAN_BOX_DASHBOARD_DETAIL_SUCCESS"] = "FETCH_THETAN_BOX_DASHBOARD_DETAIL_SUCCESS";
  ActionType["FETCH_THETAN_BOX_DASHBOARD_DETAIL_FAILURE"] = "FETCH_THETAN_BOX_DASHBOARD_DETAIL_FAILURE";
  ActionType["FETCH_GIFT_BOX_DASHBOARD_DETAIL_REQUESTED"] = "FETCH_GIFT_BOX_DASHBOARD_DETAIL_REQUESTED";
  ActionType["FETCH_GIFT_BOX_DASHBOARD_DETAIL_SUCCESS"] = "FETCH_GIFT_BOX_DASHBOARD_DETAIL_SUCCESS";
  ActionType["FETCH_GIFT_BOX_DASHBOARD_DETAIL_FAILURE"] = "FETCH_GIFT_BOX_DASHBOARD_DETAIL_FAILURE";
  ActionType["FETCH_SPECIAL_EVENT_CONFIG_REQUESTED"] = "FETCH_SPECIAL_EVENT_CONFIG_REQUESTED";
  ActionType["FETCH_SPECIAL_EVENT_CONFIG_SUCCESS"] = "FETCH_SPECIAL_EVENT_CONFIG_SUCCESS";
  ActionType["FETCH_SPECIAL_EVENT_CONFIG_FAILURE"] = "FETCH_SPECIAL_EVENT_CONFIG_FAILURE";
  ActionType["FETCH_SPECIAL_EVENT_LEADERBOARD_REQUESTED"] = "FETCH_SPECIAL_EVENT_LEADERBOARD_REQUESTED";
  ActionType["FETCH_SPECIAL_EVENT_LEADERBOARD_SUCCESS"] = "FETCH_SPECIAL_EVENT_LEADERBOARD_SUCCESS";
  ActionType["FETCH_SPECIAL_EVENT_LEADERBOARD_FAILURE"] = "FETCH_SPECIAL_EVENT_LEADERBOARD_FAILURE";
  ActionType["INVALIDATE_CHECKOUT_BOX"] = "INVALIDATE_CHECKOUT_BOX";
  ActionType["CHECKOUT_BOX_REQUESTED"] = "CHECKOUT_BOX_REQUESTED";
  ActionType["CHECKOUT_BOX_SUCCESS"] = "CHECKOUT_BOX_SUCCESS";
  ActionType["CHECKOUT_BOX_FAILURE"] = "CHECKOUT_BOX_FAILURE";
  ActionType["FETCH_LIST_NOTIFICATION_REQUESTED"] = "FETCH_LIST_NOTIFICATION_REQUESTED";
  ActionType["FETCH_LIST_NOTIFICATION_SUCCESS"] = "FETCH_LIST_NOTIFICATION_SUCCESS";
  ActionType["FETCH_LIST_NOTIFICATION_FAILURE"] = "FETCH_LIST_NOTIFICATION_FAILURE";
  ActionType["INVALIDATED_CALLOUT"] = "INVALIDATED_CALLOUT";
  ActionType["SAVE_BOX_CONFIGS"] = "SAVE_BOX_CONFIGS";
  ActionType["FETCH_HERO_DESCRIPTION_REQUESTED"] = "FETCH_HERO_DESCRIPTION_REQUESTED";
  ActionType["FETCH_HERO_DESCRIPTION_SUCCESS"] = "FETCH_HERO_DESCRIPTION_SUCCESS";
  ActionType["FETCH_HERO_DESCRIPTION_FAILURE"] = "FETCH_HERO_DESCRIPTION_FAILURE";
  ActionType["FETCH_MINT_CONFIG_REQUESTED"] = "FETCH_MINT_CONFIG_REQUESTED";
  ActionType["FETCH_MINT_CONFIG_SUCCESS"] = "FETCH_MINT_CONFIG_SUCCESS";
  ActionType["FETCH_MINT_CONFIG_FAILURE"] = "FETCH_MINT_CONFIG_FAILURE";
  ActionType["INVALIDATE_SIGNED_SIGNATURE"] = "INVALIDATE_SIGNED_SIGNATURE";
  ActionType["FETCH_SIGNED_SIGNATURE_REQUESTED"] = "FETCH_SIGNED_SIGNATURE_REQUESTED";
  ActionType["FETCH_SIGNED_SIGNATURE_SUCCESS"] = "FETCH_SIGNED_SIGNATURE_SUCCESS";
  ActionType["FETCH_SIGNED_SIGNATURE_FAILURE"] = "FETCH_SIGNED_SIGNATURE_FAILURE";
  ActionType["FETCH_RENTAL_CONFIG_REQUESTED"] = "FETCH_RENTAL_CONFIG_REQUESTED";
  ActionType["FETCH_RENTAL_CONFIG_SUCCESS"] = "FETCH_RENTAL_CONFIG_SUCCESS";
  ActionType["FETCH_RENTAL_CONFIG_FAILURE"] = "FETCH_RENTAL_CONFIG_FAILURE";
  ActionType["INVALIDATE_RENT_OUT_ITEM"] = "INVALIDATE_RENT_OUT_ITEM";
  ActionType["RENT_OUT_ITEM_REQUESTED"] = "RENT_OUT_ITEM_REQUESTED";
  ActionType["RENT_OUT_ITEM_SUCCESS"] = "RENT_OUT_ITEM_SUCCESS";
  ActionType["RENT_OUT_ITEM_FAILURE"] = "RENT_OUT_ITEM_FAILURE";
  ActionType["INVALIDATE_STOP_RENT_OUT_ITEM"] = "INVALIDATE_STOP_RENT_OUT_ITEM";
  ActionType["STOP_RENT_OUT_ITEM_REQUESTED"] = "STOP_RENT_OUT_ITEM_REQUESTED";
  ActionType["STOP_RENT_OUT_ITEM_SUCCESS"] = "STOP_RENT_OUT_ITEM_SUCCESS";
  ActionType["STOP_RENT_OUT_ITEM_FAILURE"] = "STOP_RENT_OUT_ITEM_FAILURE";
  ActionType["INVALIDATE_RENT_ITEM"] = "INVALIDATE_RENT_ITEM";
  ActionType["RENT_ITEM_REQUESTED"] = "RENT_ITEM_REQUESTED";
  ActionType["RENT_ITEM_SUCCESS"] = "RENT_ITEM_SUCCESS";
  ActionType["RENT_ITEM_FAILURE"] = "RENT_ITEM_FAILURE";
  ActionType["INVALIDATE_UNLOCK_HERO_RENTAL"] = "INVALIDATE_UNLOCK_HERO_RENTAL";
  ActionType["UNLOCK_HERO_RENTAL_REQUESTED"] = "UNLOCK_HERO_RENTAL_REQUESTED";
  ActionType["UNLOCK_HERO_RENTAL_SUCCESS"] = "UNLOCK_HERO_RENTAL_SUCCESS";
  ActionType["UNLOCK_HERO_RENTAL_FAILURE"] = "UNLOCK_HERO_RENTAL_FAILURE";
  ActionType["INVALIDATE_MINT_HERO"] = "INVALIDATE_MINT_HERO";
  ActionType["MINT_HERO_REQUESTED"] = "MINT_HERO_REQUESTED";
  ActionType["MINT_HERO_SUCCESS"] = "MINT_HERO_SUCCESS";
  ActionType["MINT_HERO_FAILURE"] = "MINT_HERO_FAILURE";
  ActionType["INVALIDATE_MINT_COSMETIC"] = "INVALIDATE_MINT_COSMETIC";
  ActionType["MINT_COSMETIC_REQUESTED"] = "MINT_COSMETIC_REQUESTED";
  ActionType["MINT_COSMETIC_SUCCESS"] = "MINT_COSMETIC_SUCCESS";
  ActionType["MINT_COSMETIC_FAILURE"] = "MINT_COSMETIC_FAILURE";
  ActionType["FETCH_CONVERT_HISTORY_REQUESTED"] = "FETCH_CONVERT_HISTORY_REQUESTED";
  ActionType["FETCH_CONVERT_HISTORY_SUCCESS"] = "FETCH_CONVERT_HISTORY_SUCCESS";
  ActionType["FETCH_CONVERT_HISTORY_FAILURE"] = "FETCH_CONVERT_HISTORY_FAILURE";
  ActionType["FETCH_CONVERT_HAS_NFT_HERO_REQUESTED"] = "FETCH_CONVERT_HAS_NFT_HERO_REQUESTED";
  ActionType["FETCH_CONVERT_HAS_NFT_HERO_SUCCESS"] = "FETCH_CONVERT_HAS_NFT_HERO_SUCCESS";
  ActionType["FETCH_CONVERT_HAS_NFT_HERO_FAILURE"] = "FETCH_CONVERT_HAS_NFT_HERO_FAILURE";
  ActionType["CONFIRM_STAKE_REQUESTED"] = "CONFIRM_STAKE_REQUESTED";
  ActionType["CONFIRM_STAKE_SUCCESS"] = "CONFIRM_STAKE_SUCCESS";
  ActionType["CONFIRM_STAKE_FAILURE"] = "CONFIRM_STAKE_FAILURE";
  ActionType["FETCH_MY_STAKING_REQUESTED"] = "FETCH_MY_STAKING_REQUESTED";
  ActionType["FETCH_MY_STAKING_SUCCESS"] = "FETCH_MY_STAKING_SUCCESS";
  ActionType["FETCH_MY_STAKING_FAILURE"] = "FETCH_MY_STAKING_FAILURE";
  ActionType["FETCH_BOX_CONFIG_REQUESTED"] = "FETCH_BOX_CONFIG_REQUESTED";
  ActionType["FETCH_BOX_CONFIG_SUCCESS"] = "FETCH_BOX_CONFIG_SUCCESS";
  ActionType["FETCH_BOX_CONFIG_FAILURE"] = "FETCH_BOX_CONFIG_FAILURE";
  ActionType["FETCH_ESTIMATE_APR_REQUESTED"] = "FETCH_ESTIMATE_APR_REQUESTED";
  ActionType["FETCH_ESTIMATE_APR_SUCCESS"] = "FETCH_ESTIMATE_APR_SUCCESS";
  ActionType["FETCH_ESTIMATE_APR_FAILURE"] = "FETCH_ESTIMATE_APR_FAILURE";
  ActionType["INVALIDATED_UNSTAKING"] = "INVALIDATED_UNSTAKING";
  ActionType["UNSTAKING_REQUESTED"] = "UNSTAKING_REQUESTED";
  ActionType["UNSTAKING_SUCCESS"] = "UNSTAKING_SUCCESS";
  ActionType["UNSTAKING_FAILURE"] = "UNSTAKING_FAILURE";
  ActionType["INVALIDATED_CLAIM_STAKING_REWARD"] = "INVALIDATED_CLAIM_STAKING_REWARD";
  ActionType["CLAIM_STAKING_REWARD_REQUESTED"] = "CLAIM_STAKING_REWARD_REQUESTED";
  ActionType["CLAIM_STAKING_REWARD_SUCCESS"] = "CLAIM_STAKING_REWARD_SUCCESS";
  ActionType["CLAIM_STAKING_REWARD_FAILURE"] = "CLAIM_STAKING_REWARD_FAILURE";
  ActionType["STAKING_CONFIG_REQUESTED"] = "STAKING_CONFIG_REQUESTED";
  ActionType["STAKING_CONFIG_SUCCESS"] = "STAKING_CONFIG_SUCCESS";
  ActionType["STAKING_CONFIG_FAILURE"] = "STAKING_CONFIG_FAILURE";
  ActionType["STAKING_STATS_REQUESTED"] = "STAKING_STATS_REQUESTED";
  ActionType["STAKING_STATS_SUCCESS"] = "STAKING_STATS_SUCCESS";
  ActionType["STAKING_STATS_FAILURE"] = "STAKING_STATS_FAILURE";
  ActionType["UPDATE_SEARCH_COSMETIC_PARAMS"] = "UPDATE_SEARCH_COSMETIC_PARAMS";
  ActionType["FETCH_SEARCH_COSMETIC_LIST_REQUESTED"] = "FETCH_SEARCH_COSMETIC_LIST_REQUESTED";
  ActionType["FETCH_SEARCH_COSMETIC_LIST_SUCCESS"] = "FETCH_SEARCH_COSMETIC_LIST_SUCCESS";
  ActionType["FETCH_SEARCH_COSMETIC_LIST_FAILURE"] = "FETCH_SEARCH_COSMETIC_LIST_FAILURE";
  ActionType["FETCH_COSMETIC_DETAIL_REQUESTED"] = "FETCH_COSMETIC_DETAIL_REQUESTED";
  ActionType["FETCH_COSMETIC_DETAIL_SUCCESS"] = "FETCH_COSMETIC_DETAIL_SUCCESS";
  ActionType["FETCH_COSMETIC_DETAIL_FAILURE"] = "FETCH_COSMETIC_DETAIL_FAILURE";
  ActionType["INVALIDATE_SELLING_COSMETIC"] = "INVALIDATE_SELLING_COSMETIC";
  ActionType["SELLING_COSMETIC_REQUESTED"] = "SELLING_COSMETIC_REQUESTED";
  ActionType["SELLING_COSMETIC_SUCCESS"] = "SELLING_COSMETIC_SUCCESS";
  ActionType["SELLING_COSMETIC_FAILURE"] = "SELLING_COSMETIC_FAILURE";
  ActionType["INVALIDATE_STOP_SELLING_COSMETIC"] = "INVALIDATE_STOP_SELLING_COSMETIC";
  ActionType["STOP_SELLING_COSMETIC_REQUESTED"] = "STOP_SELLING_COSMETIC_REQUESTED";
  ActionType["STOP_SELLING_COSMETIC_SUCCESS"] = "STOP_SELLING_COSMETIC_SUCCESS";
  ActionType["STOP_SELLING_COSMETIC_FAILURE"] = "STOP_SELLING_COSMETIC_FAILURE";
  ActionType["INVALIDATE_PURCHASE_COSMETIC"] = "INVALIDATE_PURCHASE_COSMETIC";
  ActionType["PURCHASE_COSMETIC_REQUESTED"] = "PURCHASE_COSMETIC_REQUESTED";
  ActionType["PURCHASE_COSMETIC_SUCCESS"] = "PURCHASE_COSMETIC_SUCCESS";
  ActionType["PURCHASE_COSMETIC_FAILURE"] = "PURCHASE_COSMETIC_FAILURE";
  ActionType["FETCH_COSMETIC_SALE_HISTORY_REQUESTED"] = "FETCH_COSMETIC_SALE_HISTORY_REQUESTED";
  ActionType["FETCH_COSMETIC_SALE_HISTORY_SUCCESS"] = "FETCH_COSMETIC_SALE_HISTORY_SUCCESS";
  ActionType["FETCH_COSMETIC_SALE_HISTORY_FAILURE"] = "FETCH_COSMETIC_SALE_HISTORY_FAILURE";
  ActionType["FETCH_PROJECT_DETAIL_REQUESTED"] = "FETCH_PROJECT_DETAIL_REQUESTED";
  ActionType["FETCH_PROJECT_DETAIL_SUCCESS"] = "FETCH_PROJECT_DETAIL_SUCCESS";
  ActionType["FETCH_PROJECT_DETAIL_FAILURE"] = "FETCH_PROJECT_DETAIL_FAILURE";
  ActionType["FETCH_PRIVATE_SALE_CONFIG_REQUESTED"] = "FETCH_PRIVATE_SALE_CONFIG_REQUESTED";
  ActionType["FETCH_PRIVATE_SALE_CONFIG_SUCCESS"] = "FETCH_PRIVATE_SALE_CONFIG_SUCCESS";
  ActionType["FETCH_PRIVATE_SALE_CONFIG_FAILURE"] = "FETCH_PRIVATE_SALE_CONFIG_FAILURE";
  ActionType["FETCH_PROJECT_LIST_REQUESTED"] = "FETCH_PROJECT_LIST_REQUESTED";
  ActionType["FETCH_PROJECT_LIST_SUCCESS"] = "FETCH_PROJECT_LIST_SUCCESS";
  ActionType["FETCH_PROJECT_LIST_FAILURE"] = "FETCH_PROJECT_LIST_FAILURE";
  ActionType["FETCH_THC_EXCHANGE_RATE_REQUESTED"] = "FETCH_THC_EXCHANGE_RATE_REQUESTED";
  ActionType["FETCH_THC_EXCHANGE_RATE_SUCCESS"] = "FETCH_THC_EXCHANGE_RATE_SUCCESS";
  ActionType["FETCH_THC_EXCHANGE_RATE_FAILURE"] = "FETCH_THC_EXCHANGE_RATE_FAILURE";
  ActionType["FETCH_BUY_PRIVATE_SALE_REQUESTED"] = "FETCH_BUY_PRIVATE_SALE_REQUESTED";
  ActionType["FETCH_BUY_PRIVATE_SALE_SUCCESS"] = "FETCH_BUY_PRIVATE_SALE_SUCCESS";
  ActionType["FETCH_BUY_PRIVATE_SALE_FAILURE"] = "FETCH_BUY_PRIVATE_SALE_FAILURE";
  ActionType["FETCH_SPECIAL_EVENT_REWARDS_REQUESTED"] = "FETCH_SPECIAL_EVENT_REWARDS_REQUESTED";
  ActionType["FETCH_SPECIAL_EVENT_REWARDS_SUCCESS"] = "FETCH_SPECIAL_EVENT_REWARDS_SUCCESS";
  ActionType["FETCH_SPECIAL_EVENT_REWARDS_FAILURE"] = "FETCH_SPECIAL_EVENT_REWARDS_FAILURE";
  ActionType["FETCH_USER_RANKING_REQUESTED"] = "FETCH_USER_RANKING_REQUESTED";
  ActionType["FETCH_USER_RANKING_SUCCESS"] = "FETCH_USER_RANKING_SUCCESS";
  ActionType["FETCH_USER_RANKING_FAILURE"] = "FETCH_USER_RANKING_FAILURE";
  ActionType["FETCH_TRADING_EVENT_TURN_REQUESTED"] = "FETCH_TRADING_EVENT_TURN_REQUESTED";
  ActionType["FETCH_TRADING_EVENT_TURN_SUCCESS"] = "FETCH_TRADING_EVENT_TURN_SUCCESS";
  ActionType["FETCH_TRADING_EVENT_TURN_FAILURE"] = "FETCH_TRADING_EVENT_TURN_FAILURE";
  ActionType["FETCH_JOIN_SPECIAL_EVENT_REQUESTED"] = "FETCH_JOIN_SPECIAL_EVENT_REQUESTED";
  ActionType["FETCH_JOIN_SPECIAL_EVENT_SUCCESS"] = "FETCH_JOIN_SPECIAL_EVENT_SUCCESS";
  ActionType["FETCH_JOIN_SPECIAL_EVENT_FAILURE"] = "FETCH_JOIN_SPECIAL_EVENT_FAILURE";
  ActionType["INVALIDATE_USER_PROJECT"] = "INVALIDATE_USER_PROJECT";
  ActionType["FETCH_USER_PROJECT_REQUESTED"] = "FETCH_USER_PROJECT_REQUESTED";
  ActionType["FETCH_USER_PROJECT_SUCCESS"] = "FETCH_USER_PROJECT_SUCCESS";
  ActionType["FETCH_USER_PROJECT_FAILURE"] = "FETCH_USER_PROJECT_FAILURE";
  ActionType["FETCH_FUSION_COST_REQUESTED"] = "FETCH_FUSION_COST_REQUESTED";
  ActionType["FETCH_FUSION_COST_SUCCESS"] = "FETCH_FUSION_COST_SUCCESS";
  ActionType["FETCH_FUSION_COST_FAILURE"] = "FETCH_FUSION_COST_FAILURE";
  ActionType["FETCH_HOME_BANNER_REQUESTED"] = "FETCH_HOME_BANNER_REQUESTED";
  ActionType["FETCH_HOME_BANNER_SUCCESS"] = "FETCH_HOME_BANNER_SUCCESS";
  ActionType["FETCH_HOME_BANNER_FAILURE"] = "FETCH_HOME_BANNER_FAILURE";
  ActionType["FETCH_METRIC_STATS_DASHBOARD_REQUESTED"] = "FETCH_METRIC_STATS_DASHBOARD_REQUESTED";
  ActionType["FETCH_METRIC_STATS_DASHBOARD_SUCCESS"] = "FETCH_METRIC_STATS_DASHBOARD_SUCCESS";
  ActionType["FETCH_METRIC_STATS_DASHBOARD_FAILURE"] = "FETCH_METRIC_STATS_DASHBOARD_FAILURE";
  ActionType["FETCH_HEROES_LISTED_DASHBOARD_REQUESTED"] = "FETCH_HEROES_LISTED_DASHBOARD_REQUESTED";
  ActionType["FETCH_HEROES_LISTED_DASHBOARD_SUCCESS"] = "FETCH_HEROES_LISTED_DASHBOARD_SUCCESS";
  ActionType["FETCH_HEROES_LISTED_DASHBOARD_FAILURE"] = "FETCH_HEROES_LISTED_DASHBOARD_FAILURE";
  ActionType["FETCH_HEROES_TRADED_DASHBOARD_REQUESTED"] = "FETCH_HEROES_TRADED_DASHBOARD_REQUESTED";
  ActionType["FETCH_HEROES_TRADED_DASHBOARD_SUCCESS"] = "FETCH_HEROES_TRADED_DASHBOARD_SUCCESS";
  ActionType["FETCH_HEROES_TRADED_DASHBOARD_FAILURE"] = "FETCH_HEROES_TRADED_DASHBOARD_FAILURE";
  ActionType["FETCH_COSMETIC_LISTED_DASHBOARD_REQUESTED"] = "FETCH_COSMETIC_LISTED_DASHBOARD_REQUESTED";
  ActionType["FETCH_COSMETIC_LISTED_DASHBOARD_SUCCESS"] = "FETCH_COSMETIC_LISTED_DASHBOARD_SUCCESS";
  ActionType["FETCH_COSMETIC_LISTED_DASHBOARD_FAILURE"] = "FETCH_COSMETIC_LISTED_DASHBOARD_FAILURE";
  ActionType["FETCH_COSMETIC_TRADED_DASHBOARD_REQUESTED"] = "FETCH_COSMETIC_TRADED_DASHBOARD_REQUESTED";
  ActionType["FETCH_COSMETIC_TRADED_DASHBOARD_SUCCESS"] = "FETCH_COSMETIC_TRADED_DASHBOARD_SUCCESS";
  ActionType["FETCH_COSMETIC_TRADED_DASHBOARD_FAILURE"] = "FETCH_COSMETIC_TRADED_DASHBOARD_FAILURE";
  ActionType["CONFIRM_FUSE_HERO_REQUESTED"] = "CONFIRM_FUSE_HERO_REQUESTED";
  ActionType["CONFIRM_FUSE_HERO_SUCCESS"] = "CONFIRM_FUSE_HERO_SUCCESS";
  ActionType["CONFIRM_FUSE_HERO_FAILURE"] = "CONFIRM_FUSE_HERO_FAILURE";
  ActionType["REFRESH_OUTPUT_FUSION_REQUESTED"] = "REFRESH_OUTPUT_FUSION_REQUESTED";
  ActionType["REFRESH_OUTPUT_FUSION_SUCCESS"] = "REFRESH_OUTPUT_FUSION_SUCCESS";
  ActionType["REFRESH_OUTPUT_FUSION_FAILURE"] = "REFRESH_OUTPUT_FUSION_FAILURE";
  ActionType["INVALIDATE_FUSION_SEASON"] = "INVALIDATE_FUSION_SEASON";
  ActionType["FETCH_FUSION_SEASON_REQUESTED"] = "FETCH_FUSION_SEASON_REQUESTED";
  ActionType["FETCH_FUSION_SEASON_SUCCESS"] = "FETCH_FUSION_SEASON_SUCCESS";
  ActionType["FETCH_FUSION_SEASON_FAILURE"] = "FETCH_FUSION_SEASON_FAILURE";
  ActionType["FETCH_CLAIM_PREVIOUS_FUSION_REWARD_REQUESTED"] = "FETCH_CLAIM_PREVIOUS_FUSION_REWARD_REQUESTED";
  ActionType["FETCH_CLAIM_PREVIOUS_FUSION_REWARD_SUCCESS"] = "FETCH_CLAIM_PREVIOUS_FUSION_REWARD_SUCCESS";
  ActionType["FETCH_CLAIM_PREVIOUS_FUSION_REWARD_FAILURE"] = "FETCH_CLAIM_PREVIOUS_FUSION_REWARD_FAILURE";
  ActionType["FETCH_FUSION_TURN_REQUESTED"] = "FETCH_FUSION_TURN_REQUESTED";
  ActionType["FETCH_FUSION_TURN_SUCCESS"] = "FETCH_FUSION_TURN_SUCCESS";
  ActionType["FETCH_FUSION_TURN_FAILURE"] = "FETCH_FUSION_TURN_FAILURE";
  ActionType["FETCH_BATTLE_CAP_FUSION_REQUESTED"] = "FETCH_BATTLE_CAP_FUSION_REQUESTED";
  ActionType["FETCH_BATTLE_CAP_FUSION_SUCCESS"] = "FETCH_BATTLE_CAP_FUSION_SUCCESS";
  ActionType["FETCH_BATTLE_CAP_FUSION_FAILURE"] = "FETCH_BATTLE_CAP_FUSION_FAILURE";
  ActionType["FETCH_CLAIM_CASHBACK_REQUESTED"] = "FETCH_CLAIM_CASHBACK_REQUESTED";
  ActionType["FETCH_CLAIM_CASHBACK_SUCCESS"] = "FETCH_CLAIM_CASHBACK_SUCCESS";
  ActionType["FETCH_CLAIM_CASHBACK_FAILURE"] = "FETCH_CLAIM_CASHBACK_FAILURE";
  ActionType["FETCH_FUSION_LEADERBOARD_REQUESTED"] = "FETCH_FUSION_LEADERBOARD_REQUESTED";
  ActionType["FETCH_FUSION_LEADERBOARD_SUCCESS"] = "FETCH_FUSION_LEADERBOARD_SUCCESS";
  ActionType["FETCH_FUSION_LEADERBOARD_FAILURE"] = "FETCH_FUSION_LEADERBOARD_FAILURE";
  ActionType["FETCH_SPECIAL_EVENT_LIST_REQUESTED"] = "FETCH_SPECIAL_EVENT_LIST_REQUESTED";
  ActionType["FETCH_SPECIAL_EVENT_LIST_SUCCESS"] = "FETCH_SPECIAL_EVENT_LIST_SUCCESS";
  ActionType["FETCH_SPECIAL_EVENT_LIST_FAILURE"] = "FETCH_SPECIAL_EVENT_LIST_FAILURE";
  ActionType["FETCH_QUEST_CONFIG_REQUESTED"] = "FETCH_QUEST_CONFIG_REQUESTED";
  ActionType["FETCH_QUEST_CONFIG_SUCCESS"] = "FETCH_QUEST_CONFIG_SUCCESS";
  ActionType["FETCH_QUEST_CONFIG_FAILURE"] = "FETCH_QUEST_CONFIG_FAILURE";
  ActionType["FETCH_MY_QUEST_STAGES_REQUESTED"] = "FETCH_MY_QUEST_STAGES_REQUESTED";
  ActionType["FETCH_MY_QUEST_STAGES_SUCCESS"] = "FETCH_MY_QUEST_STAGES_SUCCESS";
  ActionType["FETCH_MY_QUEST_STAGES_FAILURE"] = "FETCH_MY_QUEST_STAGES_FAILURE";
  ActionType["REFRESH_QUEST_REQUESTED"] = "REFRESH_QUEST_REQUESTED";
  ActionType["REFRESH_QUEST_SUCCESS"] = "REFRESH_QUEST_SUCCESS";
  ActionType["REFRESH_QUEST_FAILURE"] = "REFRESH_QUEST_FAILURE";
  ActionType["CLAIM_QUEST_REWARD_REQUESTED"] = "CLAIM_QUEST_REWARD_REQUESTED";
  ActionType["CLAIM_QUEST_REWARD_SUCCESS"] = "CLAIM_QUEST_REWARD_SUCCESS";
  ActionType["CLAIM_QUEST_REWARD_FAILURE"] = "CLAIM_QUEST_REWARD_FAILURE";
  ActionType["FETCH_QUEST_LEADERBOARD_REQUESTED"] = "FETCH_QUEST_LEADERBOARD_REQUESTED";
  ActionType["FETCH_QUEST_LEADERBOARD_SUCCESS"] = "FETCH_QUEST_LEADERBOARD_SUCCESS";
  ActionType["FETCH_QUEST_LEADERBOARD_FAILURE"] = "FETCH_QUEST_LEADERBOARD_FAILURE";
  ActionType["FETCH_DAILY_QUEST_REWARD_REQUESTED"] = "FETCH_DAILY_QUEST_REWARD_REQUESTED";
  ActionType["FETCH_DAILY_QUEST_REWARD_SUCCESS"] = "FETCH_DAILY_QUEST_REWARD_SUCCESS";
  ActionType["FETCH_DAILY_QUEST_REWARD_FAILURE"] = "FETCH_DAILY_QUEST_REWARD_FAILURE";
  ActionType["CLAIM_DAILY_QUEST_REWARD_REQUESTED"] = "CLAIM_DAILY_QUEST_REWARD_REQUESTED";
  ActionType["CLAIM_DAILY_QUEST_REWARD_SUCCESS"] = "CLAIM_DAILY_QUEST_REWARD_SUCCESS";
  ActionType["CLAIM_DAILY_QUEST_REWARD_FAILURE"] = "CLAIM_DAILY_QUEST_REWARD_FAILURE";
  ActionType["CLAIM_WHEEL_TICKET_REQUESTED"] = "CLAIM_WHEEL_TICKET_REQUESTED";
  ActionType["CLAIM_WHEEL_TICKET_SUCCESS"] = "CLAIM_WHEEL_TICKET_SUCCESS";
  ActionType["CLAIM_WHEEL_TICKET_FAILURE"] = "CLAIM_WHEEL_TICKET_FAILURE";
  ActionType["CLAIM_STREAK_REWARD_REQUESTED"] = "CLAIM_STREAK_REWARD_REQUESTED";
  ActionType["CLAIM_STREAK_REWARD_SUCCESS"] = "CLAIM_STREAK_REWARD_SUCCESS";
  ActionType["CLAIM_STREAK_REWARD_FAILURE"] = "CLAIM_STREAK_REWARD_FAILURE";
  ActionType["FETCH_QUEST_BOX_REQUESTED"] = "FETCH_QUEST_BOX_REQUESTED";
  ActionType["FETCH_QUEST_BOX_SUCCESS"] = "FETCH_QUEST_BOX_SUCCESS";
  ActionType["FETCH_QUEST_BOX_FAILURE"] = "FETCH_QUEST_BOX_FAILURE";
  ActionType["CLAIM_QUEST_BOX_REQUESTED"] = "CLAIM_QUEST_BOX_REQUESTED";
  ActionType["CLAIM_QUEST_BOX_SUCCESS"] = "CLAIM_QUEST_BOX_SUCCESS";
  ActionType["CLAIM_QUEST_BOX_FAILURE"] = "CLAIM_QUEST_BOX_FAILURE";
  ActionType["FETCH_COINBASE_LOOT_EVENT_CONFIGS_REQUESTED"] = "FETCH_COINBASE_LOOT_EVENT_CONFIGS_REQUESTED";
  ActionType["FETCH_COINBASE_LOOT_EVENT_CONFIGS_SUCCESS"] = "FETCH_COINBASE_LOOT_EVENT_CONFIGS_SUCCESS";
  ActionType["FETCH_COINBASE_LOOT_EVENT_CONFIGS_FAILURE"] = "FETCH_COINBASE_LOOT_EVENT_CONFIGS_FAILURE";
  ActionType["FETCH_TRADING_EVENT_SEASON_REQUESTED"] = "FETCH_TRADING_EVENT_SEASON_REQUESTED";
  ActionType["FETCH_TRADING_EVENT_SEASON_SUCCESS"] = "FETCH_TRADING_EVENT_SEASON_SUCCESS";
  ActionType["FETCH_TRADING_EVENT_SEASON_FAILURE"] = "FETCH_TRADING_EVENT_SEASON_FAILURE";
  ActionType["FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_REQUESTED"] = "FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_REQUESTED";
  ActionType["FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_SUCCESS"] = "FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_SUCCESS";
  ActionType["FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_FAILURE"] = "FETCH_CLAIM_TRADING_EVENT_DAILY_CHALLENGE_FAILURE";
  ActionType["FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_REQUESTED"] = "FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_REQUESTED";
  ActionType["FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_SUCCESS"] = "FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_SUCCESS";
  ActionType["FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_FAILURE"] = "FETCH_CLAIM_PRE_TRADING_EVENT_DAILY_REWARD_FAILURE";
  ActionType["FETCH_CLAIM_TRADING_EVENT_CASHBACK_REQUESTED"] = "FETCH_CLAIM_TRADING_EVENT_CASHBACK_REQUESTED";
  ActionType["FETCH_CLAIM_TRADING_EVENT_CASHBACK_SUCCESS"] = "FETCH_CLAIM_TRADING_EVENT_CASHBACK_SUCCESS";
  ActionType["FETCH_CLAIM_TRADING_EVENT_CASHBACK_FAILURE"] = "FETCH_CLAIM_TRADING_EVENT_CASHBACK_FAILURE";
  ActionType["FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_REQUESTED"] = "FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_REQUESTED";
  ActionType["FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_SUCCESS"] = "FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_SUCCESS";
  ActionType["FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_FAILURE"] = "FETCH_CLAIM_ALL_TRADING_EVENT_REWARDS_FAILURE";
  ActionType["FETCH_TRADING_EVENT_LEADERBOARD_REQUESTED"] = "FETCH_TRADING_EVENT_LEADERBOARD_REQUESTED";
  ActionType["FETCH_TRADING_EVENT_LEADERBOARD_SUCCESS"] = "FETCH_TRADING_EVENT_LEADERBOARD_SUCCESS";
  ActionType["FETCH_TRADING_EVENT_LEADERBOARD_FAILURE"] = "FETCH_TRADING_EVENT_LEADERBOARD_FAILURE";
  ActionType["FETCH_PREVIOUS_TRADING_EVENT_SEASON_REQUESTED"] = "FETCH_PREVIOUS_TRADING_EVENT_SEASON_REQUESTED";
  ActionType["FETCH_PREVIOUS_TRADING_EVENT_SEASON_SUCCESS"] = "FETCH_PREVIOUS_TRADING_EVENT_SEASON_SUCCESS";
  ActionType["FETCH_PREVIOUS_TRADING_EVENT_SEASON_FAILURE"] = "FETCH_PREVIOUS_TRADING_EVENT_SEASON_FAILURE";
  ActionType["FETCH_ADS_POPUP_CONFIG_REQUESTED"] = "FETCH_ADS_POPUP_CONFIG_REQUESTED";
  ActionType["FETCH_ADS_POPUP_CONFIG_SUCCESS"] = "FETCH_ADS_POPUP_CONFIG_SUCCESS";
  ActionType["FETCH_ADS_POPUP_CONFIG_FAILURE"] = "FETCH_ADS_POPUP_CONFIG_FAILURE";
  ActionType["FETCH_SAMSUNG_EVENT_REQUESTED"] = "FETCH_SAMSUNG_EVENT_REQUESTED";
  ActionType["FETCH_SAMSUNG_EVENT_SUCCESS"] = "FETCH_SAMSUNG_EVENT_SUCCESS";
  ActionType["FETCH_SAMSUNG_EVENT_FAILURE"] = "FETCH_SAMSUNG_EVENT_FAILURE";
  ActionType["FETCH_GIFTMAS_WHEEL_REQUESTED"] = "FETCH_GIFTMAS_WHEEL_REQUESTED";
  ActionType["FETCH_GIFTMAS_WHEEL_SUCCESS"] = "FETCH_GIFTMAS_WHEEL_SUCCESS";
  ActionType["FETCH_GIFTMAS_WHEEL_FAILURE"] = "FETCH_GIFTMAS_WHEEL_FAILURE";
  ActionType["FETCH_WHEEL_REWARD_REQUESTED"] = "FETCH_WHEEL_REWARD_REQUESTED";
  ActionType["FETCH_WHEEL_REWARD_SUCCESS"] = "FETCH_WHEEL_REWARD_SUCCESS";
  ActionType["FETCH_WHEEL_REWARD_FAILURE"] = "FETCH_WHEEL_REWARD_FAILURE";
  ActionType["FETCH_LOGO_COMBINE_REQUESTED"] = "FETCH_LOGO_COMBINE_REQUESTED";
  ActionType["FETCH_LOGO_COMBINE_SUCCESS"] = "FETCH_LOGO_COMBINE_SUCCESS";
  ActionType["FETCH_LOGO_COMBINE_FAILURE"] = "FETCH_LOGO_COMBINE_FAILURE";
  ActionType["FETCH_SKIN_CONFIGS_REQUESTED"] = "FETCH_SKIN_CONFIGS_REQUESTED";
  ActionType["FETCH_SKIN_CONFIGS_SUCCESS"] = "FETCH_SKIN_CONFIGS_SUCCESS";
  ActionType["FETCH_SKIN_CONFIGS_FAILURE"] = "FETCH_SKIN_CONFIGS_FAILURE";
  ActionType["FETCH_THETAN_WORLD_CONFIG_REQUESTED"] = "FETCH_THETAN_WORLD_CONFIG_REQUESTED";
  ActionType["FETCH_THETAN_WORLD_CONFIG_SUCCESS"] = "FETCH_THETAN_WORLD_CONFIG_SUCCESS";
  ActionType["FETCH_THETAN_WORLD_CONFIG_FAILURE"] = "FETCH_THETAN_WORLD_CONFIG_FAILURE";
  ActionType["ADD_SKIN_CONFIG_ENTITIES"] = "ADD_SKIN_CONFIG_ENTITIES";
  ActionType["SAVE_CONFIG_VER"] = "SAVE_CONFIG_VER";
  ActionType["SAVE_SKIN_VER"] = "SAVE_SKIN_VER";
  ActionType["DUMMY"] = "DUMMY";
})(ActionType || (ActionType = {}));

export { EipError, AppError } from '@wolffun/shared/src/types';